/**
 * ================================================================================================
 *  משימות והודעות — the office's list, and the four things a person does to one.
 * ================================================================================================
 *
 * The Owner, section 5: *"המשימות אינן רק כרטיסי התראה, ואינן מערכת לשליחת הודעות החוצה. נדרש
 * מנגנון משימות ותכתובת פנים־ארגונית, הנשמר במסד הנתונים."*
 *
 * ── WHY EVERY CONTROL ON A ROW IS DRAWN FROM `mayAct` AND NOT FROM A CAPABILITY ─────────────────
 *
 * The rule is about the ROW — assigned to me, or raised by me — and the server is the only place
 * that holds both the row and the caller. So the server decides per row and this screen renders
 * the flag. A screen that worked it out from a capability list would be a second implementation,
 * and the two would differ on exactly the row nobody thought about: an item somebody raised and
 * then assigned away.
 *
 * CANCELLING IS THE ONE EXCEPTION AND IT IS NOT AN EXCEPTION TO THAT RULE. `mayAct` is true for a
 * clerk on their own item and cancelling is still refused, because cancelling is a decision that
 * the work will not be done. The screen cannot know that from `mayAct`, so **it does not try**: it
 * offers the control and renders the server's named refusal. Hiding it would mean guessing, and a
 * guess that hides a control a person does hold is worse than a refusal that explains itself.
 *
 * ── AND WHY A MOVE SENDS `expectedState` ────────────────────────────────────────────────────────
 *
 * It reaches the store's own predicate. Two people pressing *done*, or one person pressing it
 * twice on a screen that is thirty seconds old, both reach the database — and the second is told
 * its screen is stale rather than overwriting whatever happened in between.
 */

import { useCallback, useState } from 'react';
import type { FormEvent, JSX } from 'react';

import { AppShell } from '../shell/AppShell';
import { ServerRead } from '../components/ServerRead';
import { AssigneePicker } from '../components/AssigneePicker';
import { StateBlock } from '../components/StateBlock';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { classifyActionError, type ActionFailure } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { useLocale } from '../i18n/LocaleProvider';
import type { TextKey } from '../i18n/catalogue';
import type { WorkItemDetailView, WorkItemState, WorkItemSummary } from '../api/contract';

type Scope = 'mine' | 'today' | 'all';

const SCOPES: readonly { readonly id: Scope; readonly label: TextKey }[] = [
  { id: 'mine', label: 'tasks.scope.mine' },
  { id: 'today', label: 'tasks.scope.today' },
  { id: 'all', label: 'tasks.scope.all' },
];

const STATE_LABEL: Readonly<Record<WorkItemState, TextKey>> = {
  open: 'tasks.state.open',
  in_progress: 'tasks.state.in_progress',
  done: 'tasks.state.done',
  cancelled: 'tasks.state.cancelled',
};

const EVENT_LABEL: Readonly<Record<string, TextKey>> = {
  created: 'tasks.event.created',
  assigned: 'tasks.event.assigned',
  state_changed: 'tasks.event.state_changed',
  commented: 'tasks.event.commented',
  reopened: 'tasks.event.reopened',
};

export function TenantWorkItemsScreen(): JSX.Element {
  const { t } = useLocale();
  const [scope, setScope] = useState<Scope>('mine');
  const [openUid, setOpenUid] = useState<string | null>(null);
  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const [sending, setSending] = useState(false);

  /*
   * THE READ IS KEYED BY `scope`, so switching the view asks the SERVER rather than filtering a
   * list the browser already has. The three scopes are three different predicates -- `mine` is an
   * assignee, `today` is a due date -- and a browser-side filter over one fetch would be a fourth
   * implementation of a narrowing the server already owns.
   */
  const read = useCallback(
    () => callOperation('tenant.work_item.list', { params: { scope } }),
    [scope],
  );
  const list = useServerData(read);

  async function act(what: () => Promise<unknown>): Promise<void> {
    setSending(true);
    setFailure(null);
    try {
      await what();
      list.reload();
    } catch (error) {
      setFailure(classifyActionError(error));
    } finally {
      setSending(false);
    }
  }

  return (
    <AppShell screenId="tenant-work-items" translated titleHe={t('tasks.title')}>
      <p className="screen-lead">{t('tasks.lead')}</p>

      <nav className="tabs" aria-label={t('tasks.title')}>
        {SCOPES.map((s) => (
          <button
            key={s.id}
            type="button"
            className="tabs__tab"
            aria-current={scope === s.id ? 'page' : undefined}
            onClick={() => {
              setScope(s.id);
              setOpenUid(null);
            }}
          >
            {t(s.label)}
          </button>
        ))}
      </nav>

      <NewItemForm
        sending={sending}
        onRaise={async (body) => {
          await act(() => callOperation('tenant.work_item.create', { body }));
        }}
      />

      {failure === null ? null : <ActionFailureNotice failure={failure} />}

      <ServerRead<{ items: readonly WorkItemSummary[] }>
        result={list.result}
        whatHe={t('tasks.title')}
      >
        {(data) =>
          data.items.length === 0 ? (
            <StateBlock titleHe={t('tasks.title')} bodyHe={t('tasks.empty')} tone="empty" />
          ) : (
            <ul className="list list--items">
              {data.items.map((item) => (
                <li key={item.uid} className="list__row" data-state={item.itemState}>
                  <ItemRow
                    item={item}
                    expanded={openUid === item.uid}
                    sending={sending}
                    onToggle={() => {
                      setOpenUid(openUid === item.uid ? null : item.uid);
                      /*
                       * OPENING AN ITEM MARKS IT READ, and the read mark is best-effort: a failure
                       * must not stop the item opening. Unread is the absence of a row, so the
                       * worst case is that it stays unread -- which is what it was.
                       */
                      if (openUid !== item.uid && !item.readByMe) {
                        void callOperation('tenant.work_item.read_mark', {
                          params: { item_uid: item.uid },
                          body: {},
                        }).catch(() => undefined);
                      }
                    }}
                    onMove={async (toState) => {
                      await act(() =>
                        callOperation('tenant.work_item.state', {
                          params: { item_uid: item.uid },
                          // THE PRECONDITION, from the row this screen is rendering.
                          body: { expectedState: item.itemState, toState },
                        }),
                      );
                    }}
                    onAssign={async (membershipUid) => {
                      await act(() =>
                        callOperation('tenant.work_item.assign', {
                          params: { item_uid: item.uid },
                          body: { membershipUid },
                        }),
                      );
                    }}
                    onComment={async (body) => {
                      await act(() =>
                        callOperation('tenant.work_item.comment', {
                          params: { item_uid: item.uid },
                          body: { body },
                        }),
                      );
                    }}
                  />
                </li>
              ))}
            </ul>
          )
        }
      </ServerRead>
    </AppShell>
  );
}

/**
 * RAISING ONE.
 *
 * `assignToMembershipUid` is NOT offered as a picker here, and that is a decision rather than a
 * gap: the field exists on the route and naming somebody else is refused by the server unless the
 * caller holds the management capability. A picker drawn for everybody would offer most people a
 * control that always fails. The member picker belongs on the administration surface, beside the
 * member list the office already has, and this form raises work for the person using it.
 */
function NewItemForm(props: {
  readonly sending: boolean;
  readonly onRaise: (body: {
    readonly title: string;
    readonly body: string | null;
    readonly dueOn: string | null;
  }) => Promise<void>;
}): JSX.Element {
  const { t } = useLocale();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [dueOn, setDueOn] = useState('');

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (props.sending || title.trim() === '') return;
    void props
      .onRaise({
        title: title.trim(),
        // `null` and not `''`: an empty string is a body of no characters, and no body is a
        // different fact. The column is nullable for that reason.
        body: body.trim() === '' ? null : body.trim(),
        dueOn: dueOn === '' ? null : dueOn,
      })
      .then(() => {
        setTitle('');
        setBody('');
        setDueOn('');
      });
  }

  return (
    <form className="form form--inline" onSubmit={submit}>
      <label className="field">
        <span className="field__label">{t('tasks.newTitle')}</span>
        <input
          className="field__input"
          name="title"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          maxLength={200}
          required
        />
      </label>
      <label className="field">
        <span className="field__label">{t('tasks.newBody')}</span>
        <input
          className="field__input"
          name="body"
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
          maxLength={4000}
        />
      </label>
      <label className="field">
        <span className="field__label">{t('tasks.newDue')}</span>
        <input
          className="field__input"
          name="dueOn"
          type="date"
          value={dueOn}
          onChange={(e) => setDueOn(e.currentTarget.value)}
        />
      </label>
      <button type="submit" className="btn btn--primary" disabled={props.sending}>
        {t('tasks.create')}
      </button>
    </form>
  );
}

function ItemRow(props: {
  readonly item: WorkItemSummary;
  readonly expanded: boolean;
  readonly sending: boolean;
  readonly onToggle: () => void;
  readonly onMove: (toState: WorkItemState) => Promise<void>;
  readonly onComment: (body: string) => Promise<void>;
  /** 2026-11-03. `null` clears the assignment — see `AssigneePicker`. */
  readonly onAssign: (membershipUid: string | null) => Promise<void>;
}): JSX.Element {
  const { t } = useLocale();
  const { item } = props;

  return (
    <>
      <div className="list__main">
        <button type="button" className="btn btn--quiet list__title" onClick={props.onToggle}>
          {item.title}
        </button>
        <span className="chip" data-kind={item.itemKind}>
          {t(item.itemKind === 'notice' ? 'tasks.kind.notice' : 'tasks.kind.task')}
        </span>
        <span className="chip" data-state={item.itemState}>
          {t(STATE_LABEL[item.itemState])}
        </span>
        {/* UNREAD IS A CHIP AND NOT A BOLD ROW: a person scanning a list needs to be able to say
            which of these they have not seen, and weight alone does not survive a screenshot. */}
        {item.readByMe ? null : <span className="chip chip--attention">{t('tasks.unread')}</span>}
        {item.origin === 'automatic' ? (
          <span className="chip chip--quiet">{t('tasks.origin.automatic')}</span>
        ) : null}
      </div>

      <dl className="list__meta">
        <dt>{t('tasks.due')}</dt>
        <dd>{item.dueOn ?? t('tasks.noDue')}</dd>
        <dt>{t('tasks.assignedTo')}</dt>
        <dd>{item.assignedDisplayName ?? t('tasks.unassigned')}</dd>
        <dt>{t('tasks.raisedBy')}</dt>
        {/* An automatic item is attributed to NOBODY, and the schema makes that structural. */}
        <dd>{item.createdByDisplayName ?? t('tasks.bySystem')}</dd>
      </dl>

      {props.expanded ? <ItemDetail uid={item.uid} /> : null}

      {/*
        THE CONTROLS, AND `mayAct` IS THE SERVER'S ANSWER. A row that is not this caller's renders
        no action at all -- not a disabled one: a disabled control says *you are nearly allowed*,
        and what is true is that this item is somebody else's.
      */}
      {item.mayAct && (item.itemState === 'open' || item.itemState === 'in_progress') ? (
        <div className="actions-row">
          {item.itemState === 'open' ? (
            <button
              type="button"
              className="btn"
              disabled={props.sending}
              onClick={() => void props.onMove('in_progress')}
            >
              {t('tasks.start')}
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn--primary"
            disabled={props.sending}
            onClick={() => void props.onMove('done')}
          >
            {t('tasks.complete')}
          </button>
          {/*
            OFFERED TO EVERYBODY WHO MAY ACT, AND REFUSED BY THE SERVER WITHOUT `manage`.
            See this screen's header: hiding it would mean guessing at a capability the row flag
            does not carry, and a guess that hides a control somebody does hold is worse than a
            refusal that names what is missing.
          */}
          <button
            type="button"
            className="btn btn--quiet"
            disabled={props.sending}
            onClick={() => void props.onMove('cancelled')}
          >
            {t('tasks.cancel')}
          </button>
          {/*
            WHO IT BELONGS TO, INSIDE THE EXPANDED ROW, above the correspondence. 2026-11-03:
            `tenant.work_item.assign` had been built and tested since 0027 with no control at all.
            It renders nothing for a principal without `tasks.item.manage`, which is the objection
            `reachability.ts` recorded against drawing it — answered by asking the session rather
            than by moving the control somewhere the item is not.
          */}
          <AssigneePicker item={item} sending={props.sending} onAssign={props.onAssign} />
          <CommentBox sending={props.sending} onComment={props.onComment} />
        </div>
      ) : null}
    </>
  );
}

/** The correspondence and the handling history, read together so they describe one moment. */
function ItemDetail(props: { readonly uid: string }): JSX.Element {
  const { t } = useLocale();
  const read = useCallback(
    () => callOperation('tenant.work_item.read', { params: { item_uid: props.uid } }),
    [props.uid],
  );
  const detail = useServerData(read);

  return (
    <ServerRead<WorkItemDetailView>
      result={detail.result}
      whatHe={t('tasks.comments')}
    >
      {(data) => (
        <div className="list__detail">
          <h3 className="notice__title">{t('tasks.comments')}</h3>
          {data.comments.length === 0 ? (
            <p className="notice__body">{t('tasks.noComments')}</p>
          ) : (
            <ul className="list list--comments">
              {data.comments.map((c) => (
                <li key={`${c.createdAtUtc}-${c.authorDisplayName}`}>
                  <strong>{c.authorDisplayName}</strong> <span>{c.body}</span>
                </li>
              ))}
            </ul>
          )}

          <h3 className="notice__title">{t('tasks.history')}</h3>
          <ul className="list list--history">
            {data.history.map((h) => (
              <li key={`${h.occurredAtUtc}-${h.eventKind}`}>
                <span>{t(EVENT_LABEL[h.eventKind] ?? 'tasks.event.created')}</span>
                {h.fromState !== null && h.toState !== null ? (
                  <span>
                    {' '}
                    {t(STATE_LABEL[h.fromState as WorkItemState] ?? 'tasks.state.open')} →{' '}
                    {t(STATE_LABEL[h.toState as WorkItemState] ?? 'tasks.state.open')}
                  </span>
                ) : null}
                {/* NULL ACTOR MEANS THE PRODUCT DID IT. Attributing a rule's work to whoever ran
                    the scheduler would put a person's name on a decision they did not make. */}
                <span> · {h.actorDisplayName ?? t('tasks.bySystem')}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </ServerRead>
  );
}

function CommentBox(props: {
  readonly sending: boolean;
  readonly onComment: (body: string) => Promise<void>;
}): JSX.Element {
  const { t } = useLocale();
  const [body, setBody] = useState('');

  return (
    <span className="actions-row__inline">
      <label className="field field--inline">
        <span className="field__label">{t('tasks.commentLabel')}</span>
        <input
          className="field__input"
          name="comment"
          value={body}
          placeholder={t('tasks.commentPlaceholder')}
          onChange={(e) => setBody(e.currentTarget.value)}
          maxLength={4000}
        />
      </label>
      <button
        type="button"
        className="btn"
        disabled={props.sending || body.trim() === ''}
        onClick={() => {
          void props.onComment(body.trim()).then(() => setBody(''));
        }}
      >
        {t('tasks.comment')}
      </button>
    </span>
  );
}
